import { format } from 'date-fns';
import React, { FC, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { PoolSizeAdjustmentReason } from 'shared';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, InfoCircle } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import Loader from '../../../../components/loader/index';
import { PlaceholderBar } from '../../../../components/loading-skeleton/index';
import { ExpandedTableRowsPanel } from '../../../../components/table-animations/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useSelectedPoolsStore } from '../../../../context/selected-pools.store';
import { useScreenSize, useSelectedCompany, useSharePlans } from '../../../../hooks/index';
import { Pool, PoolPlansTab } from '../../../../types/pool-plans.types';
import { adjustPoolSizeStore } from '../../adjust-pool-size/index';
import { PoolPlansPopover } from '../pool-plans-popover';
import { ScrollablePlanItem, StickyPlanItem } from './plan-item';

// Skeleton rows shown inside an expanded pool while its plans lazy-load.
const StickyPlanSkeleton: FC = () => (
  <tr className="flex h-[72px] w-full animate-pulse bg-white">
    <td className="h-[72px] w-[49px] border-b border-b-gray-100 bg-gray-50" />
    <td className="flex h-[72px] flex-1 items-center border-b border-b-gray-100 pl-12 pr-4">
      <PlaceholderBar className="h-4 w-40 rounded bg-gray-100" />
    </td>
  </tr>
);

const ScrollablePlanSkeleton: FC = () => (
  <tr className="h-[72px] w-full animate-pulse border-b border-b-gray-100 bg-white">
    <td className="p-4 text-sm text-gray-300">-</td>
    <td className="p-4 text-sm text-gray-300">-</td>
    {Array.from({ length: 8 }).map((_, index) => (
      <td className="p-4" key={index}>
        <PlaceholderBar className="h-4 w-16 rounded bg-gray-100" />
      </td>
    ))}
    <td className="relative" />
  </tr>
);

type StickyPoolItemProps = {
  date: Date;
  isSelected: boolean;
  pool: Pool;
  selectedTab: PoolPlansTab;
};

export const StickyPoolItem: FC<StickyPoolItemProps> = ({
  pool,
  isSelected,
  selectedTab,
  date,
}) => {
  const { scrollableTarget } = useScreenSize();
  const { selectedCompany } = useSelectedCompany();
  const companyId = selectedCompany?.id || '';

  const { setSelectedPool, setSelectedPlan, deselectPool, poolState } = useSelectedPoolsStore();
  const [isExpandedVisual, setExpandedVisual] = useState(false);

  const handlePoolSelected = () => {
    setSelectedPool(pool.id);
    setSelectedPlan('');
  };

  const handlePoolUnselected = () => {
    deselectPool(pool.id);
  };

  const { sharePlans, fetchNextPage, hasNextPage, isLoading } = useSharePlans({
    companyId,
    poolId: pool?.id || '',
    selectedTab,
    vestByDate: date,
    enabled: isSelected || isExpandedVisual,
  });

  const hasPlans = (pool.sharePlansCount ?? 0) > 0;

  const isTerminatedDatePassed = useMemo(() => {
    return pool?.terminationDate
      ? new Date(pool.terminationDate).getTime() < new Date().getTime()
      : false;
  }, [pool?.terminationDate]);
  return (
    <>
      <tr
        className={twMerge(
          'flex h-[72px] w-full border-l-[2px] border-l-white bg-white transition-colors',
          isSelected && 'border-l-brand-700 bg-brand-25',
        )}
      >
        <td className="relative h-[72px] border-b-[1px] border-b-gray-100">
          <div className="flex h-full w-full items-center justify-center">
            {hasPlans ? (
              <Button
                className={twMerge(
                  'h-[72px] cursor-pointer rounded-none p-4 transition-colors',
                  isSelected && 'bg-brand-50',
                )}
                onClick={isSelected ? handlePoolUnselected : handlePoolSelected}
                styleType="NONE"
              >
                <ChevronDownIcon
                  className={twMerge('transition-transform', isSelected ? 'rotate-180' : '')}
                  iconColor={isSelected ? '#2565C8' : '#344054'}
                />
              </Button>
            ) : (
              <div className="w-12 p-4 transition-colors" />
            )}
          </div>
        </td>
        <td
          className={twMerge(
            'flex h-[72px] w-full items-center gap-4 text-nowrap border-b-[1px] border-b-gray-100 px-6 py-4',
            pool?.isTerminated && isTerminatedDatePassed && 'justify-between',
          )}
        >
          <div className="flex flex-col">
            <Tooltip content={pool.name}>
              <span className="max-w-[320px] truncate text-sm font-[450] text-gray-900">
                {pool.name}
              </span>
            </Tooltip>

            <Tooltip content={pool.shareClass?.name}>
              <span className="max-w-[320px] truncate text-xs font-[450] text-gray-400">
                {pool.shareClass?.name}
              </span>
            </Tooltip>
          </div>
          {pool.isTerminated && (
            <>
              {isTerminatedDatePassed ? (
                <span className="rounded-2xl bg-fireside-50 px-2 py-[2px] text-label-sm font-[450] text-fireside-600">
                  <AppFormattedMessage id="TERMINATED" />
                </span>
              ) : (
                <HoverCard>
                  <HoverCardTrigger>
                    <InfoCircle className="size-4 cursor-pointer" />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="z-20 w-max rounded-lg bg-[#101828] px-3 py-2"
                    isShownTriangle
                    side="top"
                    triangleClassName="bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      <AppFormattedMessage id="TERMINATION_SET" />{' '}
                      {pool?.terminationDate && format(pool?.terminationDate, 'dd/MM/yyyy')}
                    </span>
                  </HoverCardContent>
                </HoverCard>
              )}
            </>
          )}
        </td>
      </tr>
      {hasPlans && (
        <ExpandedTableRowsPanel
          contentKey={isLoading ? 'loading' : 'loaded'}
          isExpanded={isSelected}
          onExpandedVisualChange={setExpandedVisual}
          syncColumns={false}
        >
          {(isSelected || isExpandedVisual) && isLoading ? (
            <StickyPlanSkeleton />
          ) : (
            (isSelected || isExpandedVisual) &&
            sharePlans && (
              <InfiniteScroll
                dataLength={sharePlans.length}
                hasMore={hasNextPage}
                loader={<Loader />}
                next={fetchNextPage}
                scrollableTarget={scrollableTarget}
              >
                {sharePlans.map((plan) => (
                  <StickyPlanItem
                    date={date}
                    isSelected={poolState.planId.includes(plan.id)}
                    key={plan.id}
                    plan={plan}
                  />
                ))}
              </InfiniteScroll>
            )
          )}
        </ExpandedTableRowsPanel>
      )}
    </>
  );
};

export type ScrollablePoolItemProps = {
  date: Date;
  isSelected: boolean;
  onDeleteClick: (id: string) => void;
  onDeletePlanClick: (id: string) => void;
  onEditClick: (id: string) => void;
  onEditPlanClick: (id: string) => void;
  onEditPlanTerminationDateClick: (id: string) => void;
  onEditTerminationDateClick: (id: string) => void;
  onViewClick: (id: string) => void;
  onViewPlanClick: (id: string) => void;
  pool: Pool;
  selectedTab: PoolPlansTab;
};

export const ScrollablePoolItem: FC<ScrollablePoolItemProps> = ({
  pool,
  isSelected,
  selectedTab,
  onEditClick,
  onViewClick,
  onDeleteClick,
  onEditPlanClick,
  onViewPlanClick,
  onDeletePlanClick,
  onEditTerminationDateClick,
  onEditPlanTerminationDateClick,
  date,
}) => {
  const { selectedCompany } = useSelectedCompany();
  const poolDilutedSharesCount =
    pool?.dillutedSharesCount && Number(pool.dillutedSharesCount)?.toLocaleString('en-US');
  const available = (pool.dillutedSharesCount - pool.granted)?.toLocaleString('en-US');
  const granted = pool.granted?.toLocaleString('en-US');
  const totalGrantVested = pool.totalGrantVested?.toLocaleString('en-US');
  const sharesVested = pool.sharesVested?.toLocaleString('en-US');
  const exercised = pool.exercised?.toLocaleString('en-US');
  const expired = pool.expired?.toLocaleString('en-US');
  const exercisable = pool.exercisable?.toLocaleString('en-US');
  const returnedToPool = pool.returnedToPool?.toLocaleString('en-US');
  const cashSettled = pool.cashSettled?.toLocaleString('en-US');
  const companyId = selectedCompany?.id || '';

  const { poolState } = useSelectedPoolsStore();
  const openAdjustPoolSize = adjustPoolSizeStore(({ open }) => open);
  const [isExpandedVisual, setExpandedVisual] = useState(false);

  const { sharePlans, isLoading } = useSharePlans({
    companyId,
    poolId: pool?.id || '',
    selectedTab,
    vestByDate: date,
    enabled: isSelected || isExpandedVisual,
  });

  const hasPlans = (pool.sharePlansCount ?? 0) > 0;

  const isTerminatedDatePassed = useMemo(() => {
    return pool?.terminationDate
      ? new Date(pool.terminationDate).getTime() < new Date().getTime()
      : false;
  }, [pool?.terminationDate]);

  return (
    <>
      <tr
        className={twMerge(
          'h-[72px] w-full border-b-[1px] border-b-gray-100 bg-white transition-colors',
          isSelected && 'bg-brand-25',
        )}
      >
        <td className="p-4 text-sm text-gray-700">{poolDilutedSharesCount || '-'}</td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !available && 'text-gray-300',
            )}
          >
            {available || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !granted && 'text-gray-300',
            )}
          >
            {granted || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !totalGrantVested && 'text-gray-300',
            )}
          >
            {totalGrantVested || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !sharesVested && 'text-gray-300',
            )}
          >
            {sharesVested || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !exercisable && 'text-gray-300',
            )}
          >
            {exercisable || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !exercised && 'text-gray-300',
            )}
          >
            {exercised || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !cashSettled && 'text-gray-300',
            )}
          >
            {cashSettled || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !returnedToPool && 'text-gray-300',
            )}
          >
            {returnedToPool || '-'}
          </span>
        </td>
        <td className="overflow-hidden p-4">
          <span
            className={twMerge(
              'max-w-[100px] truncate text-sm text-gray-700',
              !expired && 'text-gray-300',
            )}
          >
            {expired || '-'}
          </span>
        </td>
        <td className="relative">
          <PoolPlansPopover
            canBeDeleted={pool.sharePlansCount === 0}
            date={date}
            id={pool.id}
            isTerminated={isTerminatedDatePassed}
            onAdjustSizeClick={(poolId) =>
              openAdjustPoolSize({ poolId, reason: PoolSizeAdjustmentReason.FORMAL_ADJUSTMENT })
            }
            onDeleteClick={onDeleteClick}
            onEditClick={onEditClick}
            onEditTerminationDateClick={onEditTerminationDateClick}
            onViewClick={onViewClick}
            sharePlans={sharePlans || []}
            text="Pool"
          />
        </td>
      </tr>
      {hasPlans && (
        <ExpandedTableRowsPanel
          contentKey={isLoading ? 'loading' : 'loaded'}
          isExpanded={isSelected}
          onExpandedVisualChange={setExpandedVisual}
        >
          {(isSelected || isExpandedVisual) && isLoading ? (
            <ScrollablePlanSkeleton />
          ) : (
            (isSelected || isExpandedVisual) &&
            sharePlans?.map((plan) => (
              <ScrollablePlanItem
                date={date}
                isSelected={poolState.planId.includes(plan.id)}
                key={plan.id}
                onDeletePlanClick={onDeletePlanClick}
                onEditPlanClick={onEditPlanClick}
                onEditPlanTerminationDateClick={onEditPlanTerminationDateClick}
                onViewPlanClick={onViewPlanClick}
                plan={plan}
              />
            ))
          )}
        </ExpandedTableRowsPanel>
      )}
    </>
  );
};
